import { useEffect, useRef, useState } from "react";
import { gamesApi } from "@/lib/api";
import ArcadeShell from "@/components/games/ArcadeShell";

// Endless top-down pixel-art highway racer — a 1:1 port of the mobile Car Race (mobile/app/car-race.tsx).
// Drag the wheel to steer continuously, dodge traffic, grab fuel cans before the tank runs dry; speed
// and traffic density ramp with survival time. Scoring + the Games-Hub economy are unchanged.
const SPRITES = {
  player: "/games/race/player_car.png",
  fuel: "/games/race/fuel_can.png",
  crash: "/games/race/crash_boom.png",
  traffic: ["/games/race/traffic_car.png", "/games/race/traffic_car_yellow.png", "/games/race/traffic_car_green.png"],
  scenery: ["/games/race/scenery_tree.png", "/games/race/scenery_bush.png"],
};

const TICK_MS = 45;
const STEER_PX_PER_TICK = 7.2;
const WHEEL_DRAG_RANGE = 72;
const REFILL_AMOUNT = 38;
const DISTANCE_PER_MARKER = 520;
const TILE = 18;
const DASH_TILE = 28;
const SCENERY_PARALLAX = 0.55;

// Board geometry (fixed retro cabinet look, independent of app theme).
const W = 330;
const H = Math.round(W * 1.62);
const ROAD_W = Math.round(W * 0.62);
const ROAD_X = Math.round((W - ROAD_W) / 2);
const CAR_W = Math.round(ROAD_W * 0.24);
const CAR_H = Math.round(CAR_W * 1.3);
const FUEL_SIZE = Math.round(CAR_W * 0.62);
const MARKER_H = 20;
const PLAYER_Y = H - CAR_H - 30;
const ROAD_CENTER_X = ROAD_X + (ROAD_W - CAR_W) / 2;

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

function aabbHit(ax, ay, aw, ah, bx, by, bw, bh) {
  const pad = 0.16;
  return ax + aw * pad < bx + bw * (1 - pad) && ax + aw * (1 - pad) > bx + bw * pad && ay + ah * pad < by + bh * (1 - pad) && ay + ah * (1 - pad) > by + bh * pad;
}
function roadSpeedForElapsedMs(ms) {
  if (ms >= 50000) return 12;
  if (ms >= 35000) return 10.5;
  if (ms >= 22000) return 9;
  if (ms >= 12000) return 7.5;
  if (ms >= 5000) return 6;
  return 4.5;
}
function trafficSpawnEveryForElapsedMs(ms) {
  if (ms >= 50000) return 11;
  if (ms >= 35000) return 14;
  if (ms >= 22000) return 17;
  if (ms >= 12000) return 21;
  if (ms >= 5000) return 25;
  return 30;
}
const speedKmhForRoadSpeed = (rs) => Math.round(48 + rs * 11);

function useSprites() {
  const ref = useRef({ loaded: false, imgs: {} });
  const [, force] = useState(0);
  useEffect(() => {
    const urls = [SPRITES.player, SPRITES.fuel, SPRITES.crash, ...SPRITES.traffic, ...SPRITES.scenery];
    let left = urls.length;
    const imgs = {};
    urls.forEach((u) => {
      const im = new Image();
      im.onload = () => { if (--left === 0) { ref.current = { loaded: true, imgs }; force((n) => n + 1); } };
      im.onerror = () => { if (--left === 0) { ref.current = { loaded: true, imgs }; force((n) => n + 1); } };
      im.src = u;
      imgs[u] = im;
    });
  }, []);
  return ref;
}

function RaceBoard({ submitScore }) {
  const canvasRef = useRef(null);
  const spritesRef = useSprites();
  const [hud, setHud] = useState({ score: 0, speedKmh: speedKmhForRoadSpeed(4.5), fuel: 100 });
  const [gameOver, setGameOver] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [shake, setShake] = useState(0);
  const [flash, setFlash] = useState({ color: "#FFFFFF", opacity: 0 });
  const [wheelAngleUi, setWheelAngleUi] = useState(0);

  const g = useRef(null);
  const timerRef = useRef(null);
  const crashTimeoutRef = useRef(null);

  const freshState = () => ({
    carX: ROAD_CENTER_X, wheelAngle: 0, lean: 0, dragging: false, dragStartX: 0,
    fuel: 100, scroll: 0, distance: 0, traffic: [], fuelCans: [], markers: [], scenery: [],
    fuelCollected: 0, score: 0, ticks: 0, trafficSpawn: 30, fuelSpawn: 50, scenerySpawn: 20,
    markerNext: DISTANCE_PER_MARKER, nextId: 1, running: false, crashing: false, crashStart: 0, spin: 0,
  });

  const triggerFlash = (color, opacity) => {
    setFlash({ color, opacity });
    setTimeout(() => setFlash((f) => ({ ...f, opacity: 0 })), 120);
  };

  const clearTimers = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    if (crashTimeoutRef.current) clearTimeout(crashTimeoutRef.current);
    crashTimeoutRef.current = null;
  };

  const endRun = () => {
    const st = g.current;
    if (!st.running) return;
    st.running = false;
    st.crashing = true;
    st.crashStart = performance.now();
    setFinalScore(st.score);
    triggerFlash("#FF3B30", 0.42);
    // screen shake
    const seq = [-9, 9, -6, 6, 0];
    seq.forEach((v, i) => setTimeout(() => setShake(v), i * 42));
    submitScore(st.score);
    crashTimeoutRef.current = setTimeout(() => { st.crashing = false; setGameOver(true); }, 520);
  };

  const tick = () => {
    const st = g.current;
    if (!st.running) return;
    st.ticks += 1;
    const elapsedMs = st.ticks * TICK_MS;
    const roadSpeed = roadSpeedForElapsedMs(elapsedMs);

    if (!st.dragging) {
      st.wheelAngle *= 0.82;
      if (Math.abs(st.wheelAngle) < 0.01) st.wheelAngle = 0;
    }
    st.carX = clamp(st.carX + st.wheelAngle * STEER_PX_PER_TICK, ROAD_X, ROAD_X + ROAD_W - CAR_W);
    st.lean += (st.wheelAngle - st.lean) * 0.22;

    st.scroll = (st.scroll + roadSpeed) % 100000;
    st.distance += roadSpeed;
    st.fuel = Math.max(0, st.fuel - (0.15 + roadSpeed * 0.045));

    let crashed = false;
    st.traffic = st.traffic.map((t) => ({ ...t, y: t.y + roadSpeed })).filter((t) => {
      if (aabbHit(st.carX, PLAYER_Y, CAR_W, CAR_H, t.x, t.y, CAR_W, CAR_H)) crashed = true;
      return t.y < H + CAR_H;
    });

    let picked = 0;
    st.fuelCans = st.fuelCans.map((f) => ({ ...f, y: f.y + roadSpeed })).filter((f) => {
      if (aabbHit(st.carX, PLAYER_Y, CAR_W, CAR_H, f.x, f.y, FUEL_SIZE, FUEL_SIZE)) { picked += 1; return false; }
      return f.y < H + FUEL_SIZE;
    });

    let markerPassed = false;
    st.markers = st.markers.map((m) => {
      const nextY = m.y + roadSpeed;
      if (m.y <= PLAYER_Y && nextY > PLAYER_Y) markerPassed = true;
      return { ...m, y: nextY };
    }).filter((m) => m.y < H + MARKER_H);

    const sceneSpeed = roadSpeed * SCENERY_PARALLAX;
    st.scenery = st.scenery.map((s) => ({ ...s, y: s.y + sceneSpeed })).filter((s) => s.y < H + 40);

    if (--st.trafficSpawn <= 0) {
      st.traffic.push({ id: st.nextId++, x: ROAD_X + Math.random() * (ROAD_W - CAR_W), y: -CAR_H, variant: Math.floor(Math.random() * SPRITES.traffic.length) });
      st.trafficSpawn = trafficSpawnEveryForElapsedMs(elapsedMs) + Math.floor(Math.random() * 6) - 3;
    }
    if (--st.fuelSpawn <= 0) {
      st.fuelCans.push({ id: st.nextId++, x: ROAD_X + Math.random() * (ROAD_W - FUEL_SIZE), y: -FUEL_SIZE });
      st.fuelSpawn = 68 + Math.floor(Math.random() * 20);
    }
    if (st.distance >= st.markerNext) { st.markers.push({ id: st.nextId++, y: -MARKER_H }); st.markerNext += DISTANCE_PER_MARKER; }
    if (--st.scenerySpawn <= 0) {
      const side = Math.random() < 0.5 ? "left" : "right";
      st.scenery.push({ id: st.nextId++, side, x: Math.random() * Math.max(0, ROAD_X - 34), y: -40, variant: Math.floor(Math.random() * SPRITES.scenery.length) });
      st.scenerySpawn = 42 + Math.floor(Math.random() * 30);
    }

    if (picked > 0) { st.fuel = Math.min(100, st.fuel + picked * REFILL_AMOUNT); st.fuelCollected += picked; triggerFlash("#4CAF50", 0.3); }
    if (markerPassed) triggerFlash("#FFFFFF", 0.16);

    st.score = Math.floor(elapsedMs / 700) + st.fuelCollected * 3;
    setHud({ score: st.score, speedKmh: speedKmhForRoadSpeed(roadSpeed), fuel: st.fuel });
    setWheelAngleUi(st.wheelAngle);
    draw();

    if (crashed || st.fuel <= 0) endRun();
  };

  const draw = () => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const st = g.current;
    const { imgs, loaded } = spritesRef.current;
    const scroll = st.scroll;

    // grass
    ctx.fillStyle = "#2E7D32"; ctx.fillRect(0, 0, W, H);

    // parallax scenery
    if (loaded) for (const s of st.scenery) {
      const im = imgs[SPRITES.scenery[s.variant]];
      const x = s.side === "left" ? s.x : ROAD_X + ROAD_W + s.x;
      if (im) ctx.drawImage(im, x, s.y, 34, 34);
    }

    // checkered roadside strips
    for (const side of ["left", "right"]) {
      for (let i = 0; i < Math.ceil(H / TILE) + 2; i++) {
        const on = (i + Math.floor(scroll / TILE)) % 2 === 0;
        const y = i * TILE - (scroll % TILE);
        ctx.fillStyle = on ? "#1A1A1A" : "#F5F5F5";
        ctx.fillRect(side === "left" ? ROAD_X - 13 : ROAD_X + ROAD_W + 3, y, 10, TILE);
      }
    }

    // asphalt
    ctx.fillStyle = "#4A4A4A"; ctx.fillRect(ROAD_X, 0, ROAD_W, H);
    // rumble strips
    for (const side of ["left", "right"]) {
      for (let i = 0; i < Math.ceil(H / TILE) + 2; i++) {
        const on = (i + Math.floor(scroll / TILE)) % 2 === 0;
        const y = i * TILE - (scroll % TILE);
        ctx.fillStyle = on ? "#D32F2F" : "#ECEFF1";
        ctx.fillRect(side === "left" ? ROAD_X : ROAD_X + ROAD_W - 7, y, 7, TILE);
      }
    }
    // dashed center line
    ctx.fillStyle = "#ECEFF1";
    for (let i = 0; i < Math.ceil(H / DASH_TILE) + 2; i++) {
      const y = i * DASH_TILE - (scroll % DASH_TILE);
      ctx.fillRect(ROAD_X + ROAD_W / 2 - 2, y, 4, 16);
    }
    // milestone banners
    for (const m of st.markers) {
      const cell = ROAD_W / 8;
      for (let i = 0; i < 8; i++) { ctx.fillStyle = i % 2 === 0 ? "#1A1A1A" : "#F5F5F5"; ctx.fillRect(ROAD_X + i * cell, m.y, cell, MARKER_H); }
    }
    // fuel cans
    if (loaded) for (const f of st.fuelCans) { const im = imgs[SPRITES.fuel]; if (im) ctx.drawImage(im, f.x, f.y, FUEL_SIZE, FUEL_SIZE); }
    // traffic
    if (loaded) for (const t of st.traffic) { const im = imgs[SPRITES.traffic[t.variant]] || imgs[SPRITES.traffic[0]]; if (im) ctx.drawImage(im, t.x, t.y, CAR_W, CAR_H); }

    // player (with lean / crash spin)
    const pim = imgs[SPRITES.player];
    const rot = st.crashing ? ((performance.now() - st.crashStart) / 520) * 300 * (Math.PI / 180) : st.lean * 13 * (Math.PI / 180);
    ctx.save();
    ctx.translate(st.carX + CAR_W / 2, PLAYER_Y + CAR_H / 2);
    ctx.rotate(rot);
    if (pim && loaded) ctx.drawImage(pim, -CAR_W / 2, -CAR_H / 2, CAR_W, CAR_H);
    else { ctx.fillStyle = "#2E5C45"; ctx.fillRect(-CAR_W / 2, -CAR_H / 2, CAR_W, CAR_H); }
    ctx.restore();

    // crash burst
    if (st.crashing && loaded) {
      const cim = imgs[SPRITES.crash];
      const t = (performance.now() - st.crashStart) / 520;
      const sc = 0.4 + Math.min(1, t * 3) * 1.0;
      const size = CAR_W * 1.4 * sc;
      if (cim) ctx.drawImage(cim, st.carX + CAR_W / 2 - size / 2, PLAYER_Y + CAR_H / 2 - size / 2, size, size);
    }
  };

  const startRun = () => {
    clearTimers();
    g.current = freshState();
    g.current.running = true;
    setGameOver(false);
    setHud({ score: 0, speedKmh: speedKmhForRoadSpeed(4.5), fuel: 100 });
    setWheelAngleUi(0);
    draw();
    timerRef.current = setInterval(tick, TICK_MS);
  };

  useEffect(() => {
    startRun();
    return clearTimers;
  }, []);

  // Steering wheel drag (mouse + touch via Pointer Events).
  const wheelRef = useRef(null);
  const onWheelDown = (e) => {
    const st = g.current;
    if (!st) return;
    st.dragging = true;
    st.dragStartX = e.clientX;
    wheelRef.current?.setPointerCapture?.(e.pointerId);
  };
  const onWheelMove = (e) => {
    const st = g.current;
    if (!st || !st.dragging) return;
    st.wheelAngle = clamp((e.clientX - st.dragStartX) / WHEEL_DRAG_RANGE, -1, 1);
    setWheelAngleUi(st.wheelAngle);
  };
  const onWheelUp = () => { if (g.current) g.current.dragging = false; };

  const fuelColor = hud.fuel > 50 ? "#4CAF50" : hud.fuel > 20 ? "#FFA726" : "#FF3B30";

  return (
    <div className="flex w-full flex-col items-center gap-3 select-none">
      <div className="flex w-full max-w-[330px] items-center justify-around text-center">
        <div><p className="font-display text-lg font-bold" data-testid="race-score">{hud.score}</p><p className="text-[11px] text-muted-foreground">Score</p></div>
        <div><p className="font-display text-lg font-bold" data-testid="race-speed">{hud.speedKmh}</p><p className="text-[11px] text-muted-foreground">km/h</p></div>
        <div><p className="font-display text-lg font-bold" style={{ color: hud.fuel <= 20 ? "#FF3B30" : undefined }} data-testid="race-fuel">{Math.round(hud.fuel)}%</p><p className="text-[11px] text-muted-foreground">Fuel</p></div>
      </div>
      <div className="h-2 w-full max-w-[240px] overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(0, Math.min(100, hud.fuel))}%`, backgroundColor: fuelColor }} />
      </div>

      <div className="relative" style={{ transform: `translateX(${shake}px)` }}>
        <canvas ref={canvasRef} width={W} height={H} data-testid="race-canvas" className="rounded-2xl border-2 border-[#1B1B1B] shadow-lg" style={{ width: W, height: H, touchAction: "none" }} />
        <div className="pointer-events-none absolute inset-0 rounded-2xl transition-opacity" style={{ backgroundColor: flash.color, opacity: flash.opacity }} />
        {gameOver ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-2xl bg-black/60 p-6 text-center" data-testid="race-result">
            <p className="font-display text-2xl font-bold text-white">Crashed! 🏁</p>
            <p className="text-lg font-bold text-white">Score: {finalScore}</p>
            <button onClick={startRun} data-testid="race-play-again" className="mt-1 rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground">Race again</button>
          </div>
        ) : null}
      </div>

      <div
        ref={wheelRef}
        onPointerDown={onWheelDown}
        onPointerMove={onWheelMove}
        onPointerUp={onWheelUp}
        onPointerCancel={onWheelUp}
        data-testid="race-wheel"
        className="mt-1 flex h-[110px] w-[150px] cursor-grab touch-none items-center justify-center active:cursor-grabbing"
      >
        <div className="relative flex h-[92px] w-[92px] items-center justify-center rounded-full border-[8px] border-[#2B2B2B] bg-[hsl(var(--brand-tertiary))]" style={{ transform: `rotate(${wheelAngleUi * 80}deg)` }}>
          <div className="absolute h-2 w-[70px] rounded bg-[#2B2B2B]" />
          <div className="absolute h-[70px] w-2 rounded bg-[#2B2B2B]" />
          <div className="h-[22px] w-[22px] rounded-full bg-[#2B2B2B]" />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Drag the wheel to steer — dodge traffic, grab fuel cans</p>
    </div>
  );
}

export default function RaceGame() {
  return (
    <ArcadeShell title="2D Car Race" game="race" stateKey="raceState" stateFn={gamesApi.raceState} unlockFn={gamesApi.raceUnlock} scoreFn={gamesApi.raceScore}>
      {({ submitScore }) => <RaceBoard submitScore={submitScore} />}
    </ArcadeShell>
  );
}
